import React, { useMemo, useState } from "react"

import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useRouteInstancesForDate } from "@/hooks/useCollections"
import { useReexportRoutes, useRouteExports } from "@/hooks/useRouteExports"
import {
  type ExportSystem,
  type LatestExports,
  type ReexportTarget,
  type RouteExport,
  externalIdFor,
} from "@/lib/routeExports"
import type { RouteInstance } from "@/types/collections"

/** Matches MAX_TARGETS_PER_REQUEST in collections-admin-reexport. */
const MAX_TARGETS = 120

const SYSTEMS: ExportSystem[] = ["samsara", "trackpod"]

const SYSTEM_LABEL: Record<ExportSystem, string> = {
  samsara: "Samsara",
  trackpod: "Track-POD",
}

const BULK_ACTIONS: Array<{ label: string; systems: ExportSystem[] }> = [
  { label: "Export to Samsara", systems: ["samsara"] },
  { label: "Export to Track-POD", systems: ["trackpod"] },
  { label: "Export to both", systems: ["samsara", "trackpod"] },
]

/** Local calendar date, not UTC -- a 00:30 BST export still belongs to today. */
function todayIso(): string {
  const now = new Date()
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().split("T")[0]
}

function formatAttemptedAt(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  })
}

/** The id lives in the tooltip so a long Track-POD id cannot widen the column. */
const SystemCell: React.FC<{
  route: RouteInstance
  system: ExportSystem
  latest: RouteExport | undefined
}> = ({ route, system, latest }) => {
  const externalId = externalIdFor(route, system)

  const cell = (
    // min-w-0 so truncate engages; table layout is auto, so w-44 is only a hint.
    <div className="min-w-0 cursor-default space-y-0.5">
      <div className="flex min-w-0 items-center gap-1.5">
        {externalId ? (
          <CheckCircle2 size={14} className="shrink-0 text-palette-jungleGreen600" />
        ) : (
          <AlertTriangle size={14} className="shrink-0 text-amber-600" />
        )}
        <span
          className={`min-w-0 truncate text-xs ${
            externalId ? "text-palette-jungleGreen900" : "font-medium text-amber-700"
          }`}
        >
          {externalId ? "Exported" : "Not exported"}
        </span>
      </div>

      {latest && (
        <div
          className={`min-w-0 truncate text-xs ${
            latest.status === "error" ? "text-red-600" : "text-palette-jungleGreen400"
          }`}
        >
          {latest.status === "error" ? "Failed" : "Pushed"} {formatAttemptedAt(latest.attempted_at)}
        </div>
      )}
    </div>
  )

  if (!externalId && !latest) return cell

  return (
    <Tooltip>
      <TooltipTrigger asChild>{cell}</TooltipTrigger>
      <TooltipContent side="left" className="max-w-sm space-y-1">
        {externalId && (
          <div className="break-all">
            <span className="opacity-70">Route: </span>
            {externalId}
          </div>
        )}
        {latest && (
          <div>
            <span className="opacity-70">Time: </span>
            {formatAttemptedAt(latest.attempted_at)}
          </div>
        )}
        {latest?.error && <div className="text-red-300">{latest.error}</div>}
      </TooltipContent>
    </Tooltip>
  )
}

/**
 * Re-push today's routes when the overnight jobs failed. Cancelled routes are excluded so a pulled
 * route never returns to a driver; update_route because create refuses an existing external id.
 */
const ExportsContent: React.FC = () => {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [pending, setPending] = useState<{ targets: ReexportTarget[]; label: string } | null>(null)

  // Not held in state, so a page left open overnight rolls onto the new day.
  const date = todayIso()

  const { data: allRoutes = [], isLoading, error } = useRouteInstancesForDate(date)
  const routes = useMemo(
    () => allRoutes.filter((route) => route.status !== "cancelled"),
    [allRoutes]
  )

  const routeIds = useMemo(() => routes.map((route) => route.id), [routes])
  const { data: exportsByRoute = {} } = useRouteExports(routeIds)
  const reexport = useReexportRoutes()

  const visibleSelected = useMemo(
    () => routeIds.filter((id) => selected.has(id)),
    [routeIds, selected]
  )

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const allSelected = routes.length > 0 && visibleSelected.length === routes.length

  const run = (systems: ExportSystem[], label: string) => {
    const targets = visibleSelected.flatMap((routeInstanceId) =>
      systems.map((system) => ({ routeInstanceId, system }))
    )
    setPending({ targets, label })
  }

  const confirm = () => {
    if (!pending) return
    reexport.mutate(pending.targets, { onSuccess: () => setSelected(new Set()) })
    setPending(null)
  }

  const busy = reexport.isPending
  const overLimit = (systems: ExportSystem[]) =>
    visibleSelected.length * systems.length > MAX_TARGETS

  const inFlightSystems = new Set((reexport.variables ?? []).map((target) => target.system))
  const isRunning = (systems: ExportSystem[]) =>
    busy &&
    systems.length === inFlightSystems.size &&
    systems.every((system) => inFlightSystems.has(system))

  /** Overwrites are counted apart from creates: one may change a route a driver is following. */
  const pendingSummary = useMemo(() => {
    if (!pending) return []
    const byId = new Map(routes.map((route) => [route.id, route]))

    return SYSTEMS.map((system) => {
      const forSystem = pending.targets.filter((target) => target.system === system)
      if (forSystem.length === 0) return null
      const existing = forSystem.filter((target) => {
        const route = byId.get(target.routeInstanceId)
        return route ? Boolean(externalIdFor(route, system)) : false
      }).length
      return { system, existing, fresh: forSystem.length - existing }
    }).filter((entry): entry is { system: ExportSystem; existing: number; fresh: number } =>
      Boolean(entry)
    )
  }, [pending, routes])

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="mx-auto max-w-4xl space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-palette-jungleGreen900">Route Exports</h2>
          <p className="mt-1 text-sm text-palette-jungleGreen600">
            Today&apos;s routes go to Samsara and Track-POD overnight and whenever a route is saved.
            If that did not happen, select the routes here and push them again.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-palette-jungleGreen200 bg-white p-4">
          <span className="text-sm text-palette-jungleGreen600">
            {visibleSelected.length} of {routes.length} selected
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {BULK_ACTIONS.map((action, index) => (
              <Button
                key={action.label}
                variant={index === BULK_ACTIONS.length - 1 ? "default" : "outline"}
                className="gap-2"
                disabled={busy || visibleSelected.length === 0 || overLimit(action.systems)}
                onClick={() =>
                  run(action.systems, `${visibleSelected.length} route(s) — ${action.label}`)
                }
              >
                {isRunning(action.systems) && <Loader2 size={14} className="animate-spin" />}
                {action.label}
              </Button>
            ))}
          </div>
        </div>

        {overLimit(["samsara", "trackpod"]) && (
          <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <AlertTriangle size={14} />
            Too many selected to push to both systems at once (limit {MAX_TARGETS} pushes). Export
            to one system at a time, or select fewer routes.
          </div>
        )}

        <div className="rounded-lg border border-palette-jungleGreen200 bg-white">
          {isLoading ? (
            <div className="p-6 text-sm text-palette-jungleGreen600">Loading…</div>
          ) : error ? (
            <div className="p-6 text-sm text-red-600">{(error as Error).message}</div>
          ) : routes.length === 0 ? (
            <div className="p-6 text-sm text-palette-jungleGreen600">
              No routes to export today.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={() =>
                        setSelected(allSelected ? new Set() : new Set(routeIds))
                      }
                      aria-label="Select all routes"
                    />
                  </TableHead>
                  <TableHead>Route</TableHead>
                  <TableHead>Depot</TableHead>
                  <TableHead className="w-44">Samsara</TableHead>
                  <TableHead className="w-44">Track-POD</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {routes.map((route) => {
                  const latest: LatestExports = exportsByRoute[route.id] ?? {}
                  const missingSomewhere = SYSTEMS.some((system) => !externalIdFor(route, system))
                  return (
                    <TableRow key={route.id} className={missingSomewhere ? "bg-amber-50/60" : ""}>
                      <TableCell>
                        <Checkbox
                          checked={selected.has(route.id)}
                          onCheckedChange={() => toggle(route.id)}
                          aria-label={`Select ${route.code}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium text-palette-jungleGreen900">
                        {route.code}
                      </TableCell>
                      <TableCell className="text-sm text-palette-jungleGreen600">
                        {route.depot}
                      </TableCell>
                      <TableCell>
                        <SystemCell route={route} system="samsara" latest={latest.samsara} />
                      </TableCell>
                      <TableCell>
                        <SystemCell route={route} system="trackpod" latest={latest.trackpod} />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <AlertDialog open={pending !== null} onOpenChange={() => setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Export {pending?.label}?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <ul className="space-y-1">
                  {pendingSummary.map(({ system, existing, fresh }) => (
                    <li key={system}>
                      <span className="font-medium text-foreground">{SYSTEM_LABEL[system]}</span>
                      {existing > 0 && (
                        <>
                          {" — "}
                          {existing} already exported, so will be{" "}
                          <span className="font-medium text-foreground">overwritten</span>
                        </>
                      )}
                      {existing > 0 && fresh > 0 && ";"}
                      {fresh > 0 && ` ${fresh} not yet exported, so will be created`}
                    </li>
                  ))}
                </ul>
                <p>
                  This pushes to the live systems now, and drivers may see the change immediately.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirm}>Export</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default ExportsContent
